// No imports on purpose: schemas/common.schema.ts takes `clean` from here, and every web page loads that schema
// module, so importing decimal.ts (and decimal.js with it) there would put the library in the entry bundle.

/**
 * Digits a double holds reliably. MongoDB `$toDecimal` also keeps 15 significant digits of a double, so values cleaned
 * here and in a pipeline (server/helpers/decimalExpr.ts) normally agree — but they can differ by one unit in the 15th
 * digit when the value lies exactly on a tie at the 16th (`clean` rounds it away from zero, `$toDecimal` to even:
 * 1.000030517578125 → …813 here, …812 there) and on inexact results, which `dec` rounds once from the exact value while
 * a pipeline rounds through the nearest double. Never compare a JS result with a pipeline result for equality.
 *
 * Money is exact only below maxExactMagnitude(its decimals), where its minor unit is still within these 15 digits.
 */
export const DECIMAL_SIGNIFICANT_DIGITS = 15;

/**
 * Decimal places an amount of money is stored with: its currency's minor unit (2 for most currencies, 0 for JPY, 3 for
 * KWD / BHD / OMR). The ONE place money decimals come from: every `dec` money rounding passes it as its scale, and
 * `decimal-safety/no-literal-scale` refuses a number literal there. That covers contracts and contract forms today
 * (milestones, BOQ, WBS and their screens); estimates and billing still round with `toFixed(2)` and must move to `dec`
 * with the currency's decimals before a 0- or 3-decimal currency is enabled.
 * ⚠️ KNOWN GAP (owner, 2026-10-16: decide when such currencies come in; keep the code ready): portal currencies are
 * free-form (name, category, symbol) and carry no decimal setting, so every currency uses this. To switch it on:
 * 1. a `decimals` field (ISO 4217 minor unit, 0–4) on the PORTAL currency, fixed once the currency is in use (the
 *    CURRENCY_IN_USE check in currency.mongo.service.ts), read where needed through one helper (portals.currencies by
 *    projects.currency._id ?? currencyId, else this constant) — not copied into projects, whose `currency` copies go
 *    stale (the project currency switch, saveSelectedCurrencyData, $sets four fields; portal edits never reach
 *    projects). The web and MCP get it from /currency/getDefaultCurrencyDetailForProjectLevel, so its field-by-field
 *    read of the currency and `currencyDetailSchema` (currency.schema.ts) must carry it;
 * 2. every use of this constant (grep for it: the server milestone service and form milestone status, the API
 *    descriptions in contracts.schema.ts / forms.schema.ts, and the web contract and form screens — mileStones.tsx,
 *    whose columns are built once in a useRef, so build them in a useMemo keyed on the decimals; milestoneContent.tsx,
 *    WBSItemDetails.tsx, contractBoq.tsx, contractWBS.tsx, the Gantt's useContracts.tsx and the shared BOQ grid
 *    EstimateCostDatabaseItemsGridNew.tsx) takes the contract's currency decimals instead; plus every money
 *    `dec.parse`, whose default decimals is this constant;
 * 3. requests checked per currency with maxExactMagnitude(decimals): the request schemas, which cannot know the
 *    currency, refuse from DECIMAL_MAX_MAGNITUDE (the 2-decimal limit), too high for 3 decimals and too low for 0;
 * 4. stored amounts are whole minor units at 2 decimals once the milestone repair migration has run, so a 2-decimal
 *    currency needs no data change;
 * 5. a project's currency changed later to one with OTHER decimals while it holds milestones, contract money or
 *    milestone form rows is refused in saveSelectedCurrencyData (or a reviewed migration rescales them at the new
 *    decimals): amounts at the old scale would be re-rounded under what forms already use. Same-decimals changes stay.
 */
export const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * The magnitude below which an amount with `decimals` decimal places is exact in 15 significant digits: 1e13 for 2
 * (cents), 1e12 for 3 (fils), 1e15 for 0 (yen). At or above it the last decimals would be rounded away.
 */
export const maxExactMagnitude = (decimals: number): number =>
  10 ** (DECIMAL_SIGNIFICANT_DIGITS - decimals);

/**
 * maxExactMagnitude at DEFAULT_CURRENCY_DECIMALS (1e13): where checks that cannot know the currency — request schemas,
 * input parsing without a scale — refuse an amount as too large to keep its minor units.
 */
export const DECIMAL_MAX_MAGNITUDE = maxExactMagnitude(
  DEFAULT_CURRENCY_DECIMALS,
);

/**
 * Anything smaller is float residue, never a value: the finest setting the product offers is 4 decimal places
 * (`decimalPlacesDataSource`, estimateConsts.ts), six orders of magnitude coarser. The 15-digit snap alone would keep
 * `1 - 0.9 - 0.1 = -2.8e-17`, a "negative" remaining amount that fails `>= 0` checks.
 */
export const DECIMAL_RESIDUE = 1e-10;

/**
 * An operand was not a finite number (NaN, ±Infinity, or a null / undefined / text that got past the types). `dec`
 * throws it in EVERY environment, production included, so bad data fails loudly where it is used — the request or
 * page fails and the error reaches Sentry — and gets fixed at its source. Nothing turns it into a value.
 */
export class DecimalInputError extends Error {
  // A declared field, not a constructor parameter property: packages/config compiles this file with
  // `erasableSyntaxOnly`, which refuses parameter properties.
  readonly value: unknown;

  constructor(value: unknown) {
    super(`Decimal arithmetic received a non-finite value: ${String(value)}`);
    this.name = "DecimalInputError";
    this.value = value;
  }
}

/** The divisor was zero (after cleaning). The caller decides what a share of a zero total means. */
export class DecimalDivisionByZeroError extends Error {
  constructor() {
    super("Decimal division by zero");
    this.name = "DecimalDivisionByZeroError";
  }
}

/** A number of decimal places that is not a whole number from 0 to 20, such as a corrupt stored display setting. */
export class DecimalScaleError extends RangeError {
  constructor(decimalPlaces: number) {
    super(`Decimal places must be 0-20, got ${decimalPlaces}`);
    this.name = "DecimalScaleError";
  }
}

/**
 * The errors `dec` throws on bad data. A screen that catches errors to draw something else rethrows these, so bad money
 * fails the page and reaches Sentry instead of showing as an empty or raw value.
 */
export const isDecimalError = (
  error: unknown,
): error is DecimalInputError | DecimalDivisionByZeroError | DecimalScaleError =>
  error instanceof DecimalInputError ||
  error instanceof DecimalDivisionByZeroError ||
  error instanceof DecimalScaleError;

/**
 * Snap a double to its clean value: residue to 0, then 15 significant digits. `decimalNumber` uses it to clean request
 * input at parse time, and `dec` cleans every operand with it. Non-finite input comes back unchanged.
 */
export const clean = (x: number): number =>
  Math.abs(x) < DECIMAL_RESIDUE
    ? 0
    : Number(x.toPrecision(DECIMAL_SIGNIFICANT_DIGITS));
